import { Component, h, Method, Prop, VNode, VNodeData } from "@stencil/core";
import { Column } from "../table-interfaces";
import { TableCell } from "../table-cell/table-cell";
import { TableHeader } from "../table-header/table-header";
import { Table } from "../table";

@Component({
    tag: "uic-table-column-custom",
    styleUrl: "table-column-custom.scss",
    shadow: false,
})
export class TableColumnCustom implements Column {
    /** field name that is be passed to the cellTemplate function */
    @Prop() field: string;
    /** label that is displayed in the column header */
    @Prop() label: string;
    /** defines if the column should be a group */
    @Prop() group: boolean = false;
    /** defines if the column should be sortable */
    @Prop() sortable: boolean = false;
    /** field name that should be used for sorting instead of the column field */
    @Prop() sortField?: string;
    /** wrap the content of this column if required, requires to set a max-width on table (e.g. using `full-width` or css prop `--uic-table-width`) or column */
    @Prop() multiline: boolean = false;
    /** render function used to display HTML content in the cell. Keep in mind that any HTML code that could be used for XSS attacks must be escaped! */
    @Prop() cellTemplate: (
        createElement: (
            tagName: string,
            data: VNodeData | null,
            children: VNode | Array<VNode> | null
        ) => VNode,
        row: any,
        field: string
    ) => VNode;

    /** make the render function public */
    @Method()
    async getColumnMethods() {
        return {
            renderHeader: this.renderHeader.bind(this),
            renderCell: this.renderCell.bind(this),
            getSortString: this.getSortString.bind(this),
        };
    }

    private getSortString = (row: any) => {
        const sortField = this.sortField || this.field;
        if (sortField === "" || !row[sortField]) return "";
        return `${row[sortField]}`;
    };

    private renderHeader({ initialSortingCriterion: sortCriterion }: Table) {
        const sortData =
            sortCriterion?.sortProperty === this.sortField ||
            sortCriterion?.sortProperty === this.field
                ? sortCriterion
                : undefined;
        return (
            <TableHeader
                label={this.label}
                sortable={this.sortable}
                sortField={this.sortField || this.field}
                group={this.group}
                testId={this.field}
                initialSortDirection={sortData?.direction ?? ""}
            />
        );
    }

    private renderCell = (row: any) => {
        const { field, group, multiline, cellTemplate } = this;
        const cell: VNode | string | null =
            typeof cellTemplate === "function"
                ? cellTemplate(h, row, field)
                : null;
        const empty = typeof cell === "undefined" || cell === null;
        return (
            <TableCell
                group={group}
                empty={empty}
                multiline={multiline}
                testId={field}
            >
                {cell}
            </TableCell>
        );
    };
}
